import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import Typography from '@mui/material/Typography';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputAdornment from '@mui/material/InputAdornment';

import { Data, DocumentDownload, More, Security, ShieldTick, SearchNormal1, TableDocument, Trash, VolumeMute } from 'iconsax-react';
import { ChangeEvent, MouseEvent, useEffect, useState } from 'react';
import { ThemeMode } from 'types/config';
import MainCard from 'components/MainCard';
import SimpleBar from 'components/third-party/SimpleBar';
import ClientList from './ClientList';
import ChatHeader from './ChatHeader';
import Grid from '@mui/material/Grid';
import { CardActions, IconButton, Menu, MenuItem, styled, Tab, TablePagination, Tabs } from '@mui/material';
import { Button } from '@mui/material';
import ClientForm from './ClientForm';
import FormPlaceholder from './FormPlaceholder';
import useApi from 'hooks/useApi';
import { useNavigate, useParams } from 'react-router-dom';
import LoadingPlaceholder from './LoadingPlaceholder';
import { PERMISSIONS } from 'contexts/PermissionContext';
import { PermissionWrapper } from 'components/PermissionComponents';
import Auth from '../auth';
import useLegalAccess from 'hooks/useLegalAccess';
import LegalConsentTab from './LegalConsent';
import DataIntegrationsTab from './data-integrations';

const StyledTab = styled(Tab)(({ theme }) => ({
  textTransform: 'none',
  borderRadius: '8px',
  padding: '10px 8px',
  backgroundColor: theme.palette.grey[300],
  transition: 'all 0.3s ease',
  minHeight: '36px',
  marginRight: '5px',
  '&.Mui-selected': {
    backgroundColor: theme.palette.primary.main,
    color: '#fff',
    '&:hover': {
      color: '#fff'
    }
  },
  '&:hover': {
    color: theme.palette.primary.main
  }
}));

const ClientsPage = ({ handleDrawerOpen, openChatDrawer }: any) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { clientUid } = useParams();
  const { getClients, showToast, getClientByUid } = useApi();

  const [showForm, setShowForm] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [clientsLoading, setClientsLoading] = useState(true);
  // const [client, setClient] = useState(null);
  const [selectedClient, setSelectedClient] = useState<any>(null);
  const [pagination, setPagination] = useState<{ page: number; size: number }>({
    page: 0,
    size: 10
  });

  const [pageCount, setPageCount] = useState<number>(0);
  const [clients, setClients] = useState<any[]>([]);

  const matchDownLG = useMediaQuery(theme.breakpoints.down('lg'));
  const drawerBG = theme.palette.mode === ThemeMode.DARK ? 'dark.main' : 'white';
  const [anchorEl, setAnchorEl] = useState<Element | (() => Element) | null | undefined>(null);

  const [tabSelected, setTabSeelcted] = useState('details');

  // Legal & Consent and Data Integrations are Super Admin only, as decided by the server (GET legal/access).
  const { isSuperAdmin } = useLegalAccess();

  useEffect(() => {
    if (!isSuperAdmin && (tabSelected === 'legal' || tabSelected === 'integrations')) setTabSeelcted('details');
  }, [isSuperAdmin, tabSelected]);

  // set user status on status menu click
  const [search, setSearch] = useState<string | undefined>('');
  const handleSearch = async (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | undefined) => {
    const newString = event?.target.value;
    setSearch(newString);
    // Reset to first page when searching
    setPagination((prev) => ({ ...prev, page: 0 }));
  };
  const handleClickSort = (event: MouseEvent<HTMLButtonElement> | undefined) => {
    setAnchorEl(event?.currentTarget);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setTabSeelcted(newValue);
    if (newValue == 'teams') {
      navigate(`/teams/?client=${selectedClient?.uid}`);
    }
  };

  const handleCloseSort = () => {
    setAnchorEl(null);
  };

  const onClickAction = (client = null) => {
    setShowForm(false);
    setIsLoading(true);
    setSelectedClient(client);
    setTimeout(() => {
      if (!client) {
        navigate('/clients');
      }
      setShowForm(true);
      setIsLoading(false);
    });
  };

  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setPagination((prev) => ({ ...prev, page: 0, size: Number(event.target.value) }));
  };

  const resetForm = (client = null) => {
    setSelectedClient(null);
    setShowForm(false);
    navigate('/clients');
    setTabSeelcted('details');
    loadClients();
  };

  const loadClientById = async (clientId: string) => {
    setShowForm(false);
    setIsLoading(true);
    let clientResp = await getClientByUid(clientId);

    let { success, data, message } = clientResp;
    if (success) {
      onClickAction({ ...data });
      setShowForm(true);
      setIsLoading(false);
    } else {
      showToast(message, 'error');
    }
  };

  const loadClients = async () => {
    try {
      let clientResp = await getClients({
        ...pagination,
        page: pagination.page + 1,
        keyword: search || ''
      });

      const { success, data, message } = clientResp;

      if (success) {
        let { count, clients } = data;
        setPageCount(count);
        setClients(clients);
      } else {
        showToast(message, 'error');
      }

      // setIsLoading(false);
      setIsLoading(false);
      setClientsLoading(false);
    } catch (error: any) {
      showToast(error.message, 'error');
    }
  };

  useEffect(() => {
    loadClients();
  }, [pagination, search]);

  useEffect(() => {
    if (clientUid) {
      loadClientById(clientUid);
    }
  }, [clientUid]);

  return (
    <Box sx={{ display: 'flex' }}>
      <Drawer
        sx={{
          width: 250,
          flexShrink: 0,
          zIndex: { xs: 1200, lg: 0 },
          '& .MuiDrawer-paper': {
            height: matchDownLG ? '100%' : 'auto',
            width: 250,
            boxSizing: 'border-box',
            position: 'relative',
            border: 'none',
            ...(!matchDownLG && { borderRadius: '12px 0 0 12px' })
          }
        }}
        variant={matchDownLG ? 'temporary' : 'persistent'}
        anchor="left"
        open={true}
        ModalProps={{ keepMounted: true }}
      >
        <MainCard
          sx={{ bgcolor: matchDownLG ? 'transparent' : drawerBG, borderRadius: '12px 0 0 12px', borderRight: 'none' }}
          border={!matchDownLG}
          content={false}
        >
          <Box sx={{ p: 0.8, pb: 1, display: 'flex' }}>
            <PermissionWrapper permission={PERMISSIONS.ADD_EDIT_CLIENT}>
              <OutlinedInput
                fullWidth
                size="small"
                id="input-search-header"
                placeholder="Search"
                value={search}
                onChange={handleSearch}
                sx={{ '& .MuiOutlinedInput-input': { p: '0 0px 0px 8px' }, '&.MuiInputBase-root': { pr: '0' } }}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton>
                      <SearchNormal1 style={{ fontSize: 'small', height: '16px', width: '16px' }} />
                    </IconButton>
                  </InputAdornment>
                }
              />
            </PermissionWrapper>

            <PermissionWrapper permission={PERMISSIONS.ADD_EDIT_CLIENT}>
              <Button
                type="button"
                onClick={() => {
                  onClickAction(null);
                }}
                sx={{
                  marginLeft: 3,
                  bgcolor: 'secondary.dark',
                  '&:hover': {
                    bgcolor: 'secondary.main'
                  }
                }}
                variant="contained"
              >
                Add
              </Button>
            </PermissionWrapper>
          </Box>

          {
            <Box sx={{ position: 'relative' }}>
              <SimpleBar
                sx={{
                  overflowX: 'hidden',
                  height: 'calc(100vh - 250px)',
                  minHeight: 'calc(100vh - 250px)'
                }}
              >
                {/* Loader overlay for client list */}
                {clientsLoading && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      bgcolor: 'rgba(255,255,255,0.7)',
                      zIndex: 9999,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Stack alignItems="center" spacing={1}>
                      <Typography variant="caption" color="text.secondary">
                        Loading clients...
                      </Typography>
                      <Box>
                        <span>
                          <LoadingPlaceholder />
                        </span>
                      </Box>
                    </Stack>
                  </Box>
                )}
                <Box sx={{ p: 1, pt: 0, height: '40vh' }}>
                  <ClientList clients={clients} clientUid={clientUid} />
                </Box>
              </SimpleBar>
            </Box>
          }
          <CardActions>
            <TablePagination
              component="div"
              count={pageCount}
              page={pagination.page}
              onPageChange={handleChangePage}
              rowsPerPage={pagination.size}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage=""
            />
          </CardActions>
        </MainCard>
      </Drawer>
      <MainCard
        content={false}
        sx={{
          bgcolor: theme.palette.mode === ThemeMode.DARK ? 'dark.main' : 'grey.50',
          pt: 2,
          borderRadius: '0 12px 12px 0',
          width: '100%'
        }}
      >
        {showForm && (
          <Grid container spacing={3}>
            <Grid item xs={12} sx={{ bgcolor: 'background.paper', pr: 2, pb: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
              <Grid container justifyContent="space-between" spacing={1.5}>
                <Grid item>
                  <ChatHeader selected={selectedClient} loading={false} user={null} handleDrawerOpen={handleDrawerOpen} />
                </Grid>
                <Grid item>
                  <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={1}>
                    {clientUid && (
                      <Tabs
                        sx={{
                          '& .MuiTabs-indicator': {
                            display: 'none'
                          }
                        }}
                        value={tabSelected}
                        onChange={handleTabChange}
                        aria-label="basic tabs example"
                      >
                        <StyledTab
                          icon={<TableDocument size="24" color={tabSelected != 'details' ? theme.palette.text.primary : '#fff'} />}
                          iconPosition="start"
                          label="Details"
                          value="details"
                          sx={{
                            backgroundColor: tabSelected === 'details' ? 'secondary.dark' : theme.palette.grey[300],
                            '&:hover': {
                              backgroundColor: tabSelected === 'details' ? theme.palette.grey[400] : 'secondary.main',
                              color: 'text.main'
                            },
                            '&.Mui-selected': {
                              backgroundColor: theme.palette.secondary.dark,
                              color: '#fff',
                              '&:hover': {
                                color: '#fff'
                              }
                            }
                          }}
                        />
                        {/* 2 */}
                        {/* <StyledTab
                          icon={<Shield size="24" color={tabSelected != 'roles' ? theme.palette.text.primary : '#fff'} />}
                          iconPosition="start"
                          label="Roles"
                          value="roles"
                          sx={{
                            backgroundColor: tabSelected === 'roles' ? 'secondary.dark' : theme.palette.grey[300],
                            // color: tabSelected === 'details' ? theme.palette.text.primary : 'text.main',
                            '&:hover': {
                              backgroundColor: tabSelected === 'roles' ? theme.palette.grey[400] : 'secondary.main',
                              color: 'text.main'
                            },
                            '&.Mui-selected': {
                              backgroundColor: theme.palette.secondary.dark,
                              color: '#fff',
                              '&:hover': {
                                color: '#fff'
                              }
                            }
                          }}
                        /> */}
                        <StyledTab
                          icon={<Security size="24" color={tabSelected != 'auth' ? theme.palette.text.primary : '#fff'} />}
                          iconPosition="start"
                          label="Auth"
                          value="auth"
                          sx={{
                            backgroundColor: tabSelected === 'auth' ? 'secondary.dark' : theme.palette.grey[300],
                            // color: tabSelected === 'details' ? theme.palette.text.primary : 'text.main',
                            '&:hover': {
                              backgroundColor: tabSelected === 'auth' ? theme.palette.grey[400] : 'secondary.main',
                              color: 'text.main'
                            },
                            '&.Mui-selected': {
                              backgroundColor: theme.palette.secondary.dark,
                              color: '#fff',
                              '&:hover': {
                                color: '#fff'
                              }
                            }
                          }}
                        />
                        {isSuperAdmin && (
                          <StyledTab
                            icon={<ShieldTick size="24" color={tabSelected !== 'legal' ? theme.palette.text.primary : '#fff'} />}
                            iconPosition="start"
                            label="Legal & Consent"
                            value="legal"
                            sx={{
                              backgroundColor: tabSelected === 'legal' ? 'secondary.dark' : theme.palette.grey[300],
                              '&:hover': {
                                backgroundColor: tabSelected === 'legal' ? theme.palette.grey[400] : 'secondary.main',
                                color: 'text.main'
                              },
                              '&.Mui-selected': {
                                backgroundColor: theme.palette.secondary.dark,
                                color: '#fff',
                                '&:hover': {
                                  color: '#fff'
                                }
                              }
                            }}
                          />
                        )}
                        {isSuperAdmin && (
                          <StyledTab
                            icon={<Data size="24" color={tabSelected !== 'integrations' ? theme.palette.text.primary : '#fff'} />}
                            iconPosition="start"
                            label="Data Integrations"
                            value="integrations"
                            sx={{
                              backgroundColor: tabSelected === 'integrations' ? 'secondary.dark' : theme.palette.grey[300],
                              '&:hover': {
                                backgroundColor: tabSelected === 'integrations' ? theme.palette.grey[400] : 'secondary.main',
                                color: 'text.main'
                              },
                              '&.Mui-selected': {
                                backgroundColor: theme.palette.secondary.dark,
                                color: '#fff',
                                '&:hover': {
                                  color: '#fff'
                                }
                              }
                            }}
                          />
                        )}
                      </Tabs>
                    )}
                    <IconButton sx={{ display: 'none' }} onClick={handleClickSort} size="large" color="secondary">
                      {' '}
                      <More />
                    </IconButton>
                    <Menu
                      id="simple-menu"
                      anchorEl={anchorEl}
                      keepMounted
                      open={Boolean(anchorEl)}
                      onClose={handleCloseSort}
                      anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right'
                      }}
                      transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right'
                      }}
                      sx={{ p: 0, '& .MuiMenu-list': { p: 0 } }}
                    >
                      <MenuItem onClick={handleCloseSort}>
                        <DocumentDownload style={{ paddingRight: 8 }} />
                        <Typography>Archive</Typography>
                      </MenuItem>
                      <MenuItem onClick={handleCloseSort}>
                        <VolumeMute style={{ paddingRight: 8 }} />
                        <Typography>Muted</Typography>
                      </MenuItem>
                      <MenuItem onClick={handleCloseSort}>
                        <Trash style={{ paddingRight: 8 }} />
                        <Typography>Delete</Typography>
                      </MenuItem>
                    </Menu>
                  </Stack>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        )}

        {showForm && tabSelected == 'details' && <ClientForm selected={selectedClient} resetForm={resetForm} clientUid={clientUid} />}
        {/* {showForm && tabSelected == 'users' && selectedClient && (
          <ManageClientUsers {...{ selected: selectedClient, resetForm, clientUid }} />
        )} */}
        {/* {showForm && tabSelected == 'roles' && selectedClient && clientUid && (
          <ClientRoles clientUid={clientUid} selected={selectedClient} />
        )} */}
        {!showForm && !isLoading && <FormPlaceholder onClickAction={onClickAction} />}
        {isLoading && <LoadingPlaceholder />}
        {showForm && tabSelected == 'auth' && <Auth clientUid={clientUid} />}
        {showForm && tabSelected === 'legal' && isSuperAdmin && clientUid && (
          <LegalConsentTab clientUid={clientUid} clientName={selectedClient?.name} />
        )}
        {showForm && tabSelected === 'integrations' && isSuperAdmin && clientUid && <DataIntegrationsTab clientUid={clientUid} />}
      </MainCard>
    </Box>
  );
};

export default ClientsPage;
